"use client";

import * as React from "react";
import { cn } from "@/lib/cn";
import { storyScreens } from "@/data/system-screens";
import Eyebrow from "@/components/primitives/Eyebrow";
import Reveal from "@/components/primitives/Reveal";
import SystemWindow from "./SystemWindow";

/**
 * Narrativa com rolagem: o texto de cada módulo passa à esquerda e a janela fixa à direita
 * troca de tela conforme a etapa que está no centro da viewport. No celular, cada etapa
 * traz a sua própria janela.
 */
export default function SystemStory() {
  const [active, setActive] = React.useState(0);
  const stepRefs = React.useRef<(HTMLLIElement | null)[]>([]);

  React.useEffect(() => {
    const els = stepRefs.current.filter((el): el is HTMLLIElement => !!el);
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number((e.target as HTMLElement).dataset.index));
        });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div>
      <div className="mx-auto max-w-3xl text-center">
        <Reveal>
          <Eyebrow>Na prática</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h3 className="h-display mt-5 text-[clamp(1.75rem,4vw,2.75rem)] text-fg">
            Escola, frota e relatórios <span className="text-gradient">no mesmo sistema.</span>
          </h3>
        </Reveal>
      </div>

      <div className="mt-12 grid gap-10 lg:mt-16 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:gap-14">
        <ol className="flex flex-col gap-16 lg:gap-0">
          {storyScreens.map((s, i) => (
            <li
              key={s.id}
              ref={(el) => {
                stepRefs.current[i] = el;
              }}
              data-index={i}
              className="relative lg:flex lg:min-h-[80vh] lg:items-center"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "absolute -left-5 top-1/2 hidden w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-b from-electric to-violet transition-all duration-500 motion-reduce:transition-none lg:block",
                  active === i ? "h-3/5 opacity-100" : "h-0 opacity-0"
                )}
              />
              <div
                className={cn(
                  "transition-opacity duration-500 motion-reduce:transition-none",
                  active === i ? "lg:opacity-100" : "lg:opacity-40"
                )}
              >
                <span className="text-xs font-semibold tabular-nums tracking-[0.14em] text-electric">
                  {String(i + 1).padStart(2, "0")} / {String(storyScreens.length).padStart(2, "0")}
                </span>
                <h4 className="mt-3 text-2xl font-semibold tracking-tight text-fg sm:text-3xl">
                  {s.title}
                </h4>
                <p className="mt-3 max-w-md text-base leading-relaxed text-muted">{s.text}</p>
                {s.bullets && (
                  <ul className="mt-5 space-y-2.5">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-fg/85">
                        <span
                          aria-hidden="true"
                          className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-electric shadow-[0_0_10px_rgb(var(--electric)_/_0.8)]"
                        />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}

                {/* celular/tablet: janela própria de cada etapa */}
                <div className="mt-8 lg:hidden">
                  <SystemWindow screens={[s]} active={0} sizes="(min-width: 640px) 90vw, 96vw" />
                </div>
              </div>
            </li>
          ))}
        </ol>

        {/* desktop: janela fixa que troca de tela */}
        <div className="hidden lg:block">
          <div className="sticky top-[max(6rem,calc(50vh-13rem))]">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-y-6 inset-x-[6%] -z-10 blur-3xl"
              style={{
                background:
                  "radial-gradient(closest-side, rgb(var(--brand) / 0.32), rgb(var(--violet) / 0.12) 65%, transparent)",
              }}
            />
            <SystemWindow screens={storyScreens} active={active} sizes="(min-width: 1280px) 700px, 60vw" />
          </div>
        </div>
      </div>
    </div>
  );
}
